import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { OrganizationAdminState } from "@/types/store";
import { Organization } from "@/types/organization";
import {
  addStreamlineTokens,
  createOrganization,
  deactivateOrganization,
  editOrganization,
  getOrganizations,
  reactivateOrganization,
  uploadOrganizationLogo,
} from "@/api/organization/thunks";

const initialState: OrganizationAdminState = {
  organizations: [],
  total: 0,
  page: 1,
  hasNextPage: false,
  loading: false,
  error: null,
};

const organizationAdminSlice = createSlice({
  name: "OrganizationAdminSlice",
  initialState,
  reducers: {
    setOrganizations: (state, action: PayloadAction<Organization[]>) => {
      state.organizations = action.payload;
    },
    setTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrganizations.pending, (state) => {
        state.loading = true;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.organizations = action.payload.organizations;
        state.hasNextPage = action.payload.hasNextPage;
        state.loading = false;
        state.total = action.payload.total;
        state.error = null;
      })
      .addCase(getOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(editOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(editOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.organizations = state.organizations.map((organization) =>
          organization.id === action.payload.id ? action.payload : organization
        );
      })
      .addCase(editOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(createOrganization.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadOrganizationLogo.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadOrganizationLogo.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(uploadOrganizationLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(addStreamlineTokens.pending, (state) => {
        state.loading = true;
      })
      .addCase(addStreamlineTokens.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(addStreamlineTokens.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deactivateOrganization.fulfilled, (state, action) => {
        const organizationId = action.meta.arg;
        state.organizations = state.organizations.map((organization) =>
          organization.id === organizationId
            ? { ...organization, status: "Inactive" }
            : organization
        );
        state.loading = false;
        state.error = null;
      })
      .addCase(deactivateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(reactivateOrganization.fulfilled, (state, action) => {
        const organizationId = action.meta.arg;
        state.organizations = state.organizations.map((organization) =>
          organization.id === organizationId
            ? { ...organization, status: "Active" }
            : organization
        );
        state.loading = false;
        state.error = null;
      })
      .addCase(reactivateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setOrganizations, setTotal, setPage } =
  organizationAdminSlice.actions;
export default organizationAdminSlice.reducer;
